/* ============================================
   Page Drawer - Dynamic Content Drawer
   Extracted from snippets/c-page-drawer.liquid (browser-cached, deferred).

   Content is fetched from a minimal alternate template:
     GET /pages/<handle>?view=drawer  ->  templates/page.drawer.liquid
   which returns only { title, page.content } inside a known wrapper.
   Empty/section-built pages (no page.content) degrade to normal navigation.

   Focus containment relies on native <dialog>.showModal() inertness;
   the previous manual focus trap and DIY HTML sanitizer were removed
   (same-origin, own controlled template, merchant-trusted page.content).

   Content is injected with innerHTML, so <script> in a drawer view never
   runs: drawer views (c-ingredients-drawer, c-feeding-drawer) stay
   script-free (CSS :has() for interaction) and link their own CSS. Any
   popstate closes an open drawer, so a fragment link inside drawer content
   closes it too.

   Bind dismissal handlers as `() => close()`, never a bare `close`
   reference: the event object lands in `skipHistory` as a truthy value,
   skips the history pop and orphans one same-URL entry per close.

   Auto-interception of plain /pages/ links skips anything inside
   `header, .header, [class*="header"]`, which includes every BEM `*__header`
   element; a page link there needs an explicit data-page-drawer (on it or an
   ancestor, which is checked first).

   `data-no-drawer` and `data-roots-chat` (chat links) opt a link out.

   A /pages/ link inside open drawer content swaps the drawer to that page
   (swap()), so a reader who opened it on the PDP stays there; a link to the
   page already shown navigates. A page with no drawer view still navigates.

   data-page-drawer-element="<id>" shows an element already on this page in the
   drawer instead of fetching one (the PDP's Judge.me review widget). The element
   itself moves into the drawer, so its scripts and state keep working, and it goes
   back to its place when the drawer closes. Its href should be a #<id> anchor: with
   no such element on the page, or no JS, the link scrolls to it. Judge.me opens its
   write-review and media modals on <body>, where this modal would leave them inert,
   so the drawer closes when one appears.
   ============================================ */
(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    animationDuration: 250,
    fetchTimeout: 8000,
    debounceDelay: 100,
    selectors: {
      trigger: '[data-page-drawer]',
      dialog: '#page-drawer-dialog',
      closeBtn: '[data-page-drawer-close]',
      title: '#page-drawer-title',
      body: '.page-drawer__body',
    },
    classes: {
      closing: 'is-closing',
      bodyLock: 'page-drawer-open',
    },
  };

  // State
  const cache = new Map(); // completed results, keyed by handle
  const inflight = new Map(); // pending fetch promises, keyed by handle (dedupe)
  let cacheGeneration = 0; // bumped by clearCache(); a fetch only writes back if its snapshot still matches, so a prefetch in flight during clearCache() can't repopulate the cleared cache
  let pageAllowsAutoLinks = false;
  let dialog = null;
  let titleEl = null;
  let bodyEl = null;
  let closeBtn = null;
  let abortController = null;
  let previousFocus = null;
  let scrollY = 0;
  let isClosing = false;
  let lastOpenTime = 0;
  let currentHandle = null;
  let prevScrollRestoration = null; // browser's scrollRestoration, saved while the drawer owns scroll
  let skipHistoryOnClose = false; // true when close() was driven by popstate (Back already navigated)
  let suppressDialogClose = false; // true while we programmatically close the modal during a teardown (empty-page hard-nav or bfcache reset) so the next onDialogClose skips its scroll/history/focus work
  let borrowed = null; // { el, marker } while a page element is shown in the drawer (openElement)
  let closeTimer = null; // handle for close()'s slide-out timeout, so it can be cancelled (bfcache resumes pending timers; a stale one must not fire dialog.close() after reset/reopen)

  /**
   * Initialize
   */
  function init() {
    dialog = document.querySelector(CONFIG.selectors.dialog);
    if (!dialog) return;

    // Clear a stale { pageDrawer } history entry carried into a FRESH load by an
    // interrupted navigation or the empty-page hard-navigation fallback. (A true
    // bfcache back/forward restore does not re-run init(); that case is handled by
    // the pageshow handler below.) Mirrors cart-drawer.js. Without it, a leaked entry
    // makes close() pop a real page entry.
    if (history.state && history.state.pageDrawer) {
      history.replaceState(null, '', window.location.href);
    }

    titleEl = dialog.querySelector(CONFIG.selectors.title);
    bodyEl = dialog.querySelector(CONFIG.selectors.body);
    closeBtn = dialog.querySelector(CONFIG.selectors.closeBtn);

    // Event delegation for triggers
    document.addEventListener('click', onTriggerClick, { passive: false });
    // Capture phase: an element trigger's href is a #anchor, and c-anchor-scroll's own document
    // listener would scroll to it unless this has already claimed the click.
    document.addEventListener('click', onElementTriggerClick, { capture: true });

    // Precompute whether automatic /pages/ link detection applies on this page
    // (the template is static for the page's lifetime, so resolve it once instead
    // of on every hover event).
    const mainContent = document.querySelector('#MainContent, main');
    const templateAttr = mainContent?.dataset?.template || document.body.dataset?.template || '';
    pageAllowsAutoLinks =
      document.body.classList.contains('template-product') ||
      templateAttr.includes('product') ||
      document.body.classList.contains('template-index') ||
      templateAttr === 'index' ||
      window.location.pathname === '/';

    // Prefetch on intent: warm the cache when a trigger is hovered, keyboard-
    // focused, or touch-started so the first open is instant instead of showing
    // the loading spinner. All passive — these never block or alter the click.
    document.addEventListener('pointerover', onPrefetchIntent, { passive: true });
    document.addEventListener('focusin', onPrefetchIntent, { passive: true });
    document.addEventListener('touchstart', onPrefetchIntent, { passive: true });

    // Close handlers
    if (closeBtn) {
      closeBtn.addEventListener('click', () => close());
    }
    dialog.addEventListener('click', onBackdropClick);
    // A chat link in drawer content (c-quality-drawer) opens the chat widget, which the modal would
    // otherwise leave inert behind it; c-chat-link's document listener opens it after this closes.
    dialog.addEventListener('click', (e) => {
      if (e.target.closest('[data-roots-chat]')) close();
    });
    dialog.addEventListener('click', () => {
      if (!borrowed) return;
      setTimeout(() => {
        const modal = [...document.querySelectorAll('.jdgm-review-widget-modal')].find(
          (m) => getComputedStyle(m).display !== 'none'
        );
        if (!modal) return;
        previousFocus = null; // focus belongs to Judge.me's modal now, not the trigger
        close();
      }, 50);
    });
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('close', onDialogClose);

    // Handle browser back button
    window.addEventListener('popstate', onPopState);

    // bfcache restore: a page cached mid-interaction comes back with its DOM AND
    // this module's JS state preserved, so it can restore with a stuck-open modal,
    // a locked body, scrollRestoration still 'manual', or a stale { pageDrawer }
    // entry. init() does NOT re-run on a persisted restore, so reset to a clean
    // closed baseline here. (pageshow also fires on the initial load with
    // persisted=false, which init() already handled — skip that.)
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      // Reset state preserved by the cached document to a clean closed baseline.
      // Default suppressDialogClose to false first, then set it true ONLY when a close is
      // actually pending (below).
      isClosing = false;
      skipHistoryOnClose = false;
      currentHandle = null;
      suppressDialogClose = false;
      // If a modal was preserved open, close it. The dialog 'close' event fires as a
      // LATER task, not synchronously, so we must NOT reset suppressDialogClose right after
      // close(); the async onDialogClose reads it, early-returns (skipping its now-stale
      // scrollTo / focus / history work), and clears the flag itself.
      if (dialog.open) {
        suppressDialogClose = true;
        dialog.close();
      }
      // If the page was cached mid-close, close()'s slide-out timeout is still pending;
      // bfcache resumes it on restore, and it would call dialog.close() after we've reset
      // (or even after a reopen). Cancel it here and strip the is-closing class ourselves.
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      dialog.classList.remove(CONFIG.classes.closing);
      // On iOS the lock is position:fixed with a negative top offset, so the real document
      // scroll is 0 while the drawer holds the page. Simply unlocking recreates the original
      // top-jump. onDialogClose normally restores scrollY, but here suppressDialogClose
      // short-circuits it, so restore inline. Capture the lock state BEFORE unlocking and gate
      // on it: if the drawer was already closed when cached, scrollY is stale and an
      // unconditional restore would yank the page to an old position. Desktop (overflow:hidden
      // preserved scroll) makes this a harmless no-op; iOS needs it.
      const wasLocked = document.body.classList.contains(CONFIG.classes.bodyLock);
      document.body.classList.remove(CONFIG.classes.bodyLock);
      document.body.style.removeProperty('--page-drawer-scroll-position');
      if (wasLocked) {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      }
      if (prevScrollRestoration !== null) {
        history.scrollRestoration = prevScrollRestoration;
        prevScrollRestoration = null;
      }
      // replaceState() clears the { pageDrawer } state but cannot remove the extra
      // same-URL entry that open() pushed for Back-to-close. KNOWN LIMITATION: if the
      // page was cached with the drawer open, that stale entry survives (one wasted
      // Back press). Do NOT "fix" it with history.back()/go(-1) here — pageshow is
      // inside the restore lifecycle, so a traversal from it is riskier than the entry.
      if (history.state && history.state.pageDrawer) {
        history.replaceState(null, '', window.location.href);
      }
    });
  }

  /**
   * Resolve a page-drawer trigger from an event target.
   * Shared by the click handler and the prefetch handlers so the prefetched
   * handle always matches the one a click would open (no drift between them).
   * @param {EventTarget} target
   * @returns {{trigger: Element, handle: string, title: string|null}|null}
   */
  function resolveTrigger(target) {
    if (!target || typeof target.closest !== 'function') return null;

    // Method 1: Explicit data-page-drawer attribute (works EVERYWHERE,
    // including button-styled links — runs before the .button exclusion below)
    const explicit = target.closest(CONFIG.selectors.trigger);
    if (explicit) {
      return {
        trigger: explicit,
        handle: explicit.dataset.pageDrawer,
        title: explicit.dataset.pageDrawerTitle || null,
      };
    }

    // Page links inside an open drawer swap it, on any page (resolveDrawerLink).
    const drawerLink = target.closest('.page-drawer__dialog a[href*="/pages/"]');
    if (drawerLink) return resolveDrawerLink(drawerLink);

    // Method 2: Automatic links pointing to /pages/xxx — only on product/home
    // pages; excludes the drawer itself, footer/header/nav, and buttons.
    if (!pageAllowsAutoLinks) return null;

    const link = target.closest('a[href*="/pages/"]');
    if (!link) return null;
    if (link.closest('.page-drawer__dialog')) return null;
    if (link.hasAttribute('data-no-drawer')) return null;
    // Chat links (snippets/c-chat-link.liquid) open the chat; their /pages/contact href is only the no-JS fallback.
    if (link.hasAttribute('data-roots-chat')) return null;
    if (link.closest('footer, .footer, [class*="footer"]')) return null;
    if (link.closest('header, .header, [class*="header"]')) return null;
    if (link.closest('nav, .nav, [class*="navigation"], [class*="menu"]')) return null;
    if (link.closest('.button, [class*="btn"], [role="button"]')) return null;

    const match = link.getAttribute('href').match(/\/pages\/([^?#/]+)/);
    if (!match) return null;
    return { trigger: link, handle: match[1], title: link.dataset.pageDrawerTitle || null };
  }

  /**
   * A /pages/ link inside open drawer content swaps the drawer to that page, so the reader
   * stays where they opened it (the PDP). A link to the page already shown navigates, as do
   * data-no-drawer and chat links.
   * @param {Element} link
   */
  function resolveDrawerLink(link) {
    if (!link.closest(CONFIG.selectors.body)) return null;
    if (link.hasAttribute('data-no-drawer') || link.hasAttribute('data-roots-chat')) return null;
    const match = link.getAttribute('href').match(/\/pages\/([^?#/]+)/);
    if (!match || encodeURIComponent(match[1]) === currentHandle) return null;
    return { trigger: link, handle: match[1], title: link.dataset.pageDrawerTitle || null };
  }

  /**
   * Handle trigger clicks with debounce
   * Supports both data-page-drawer attribute AND automatic /pages/ links
   */
  function onTriggerClick(e) {
    const resolved = resolveTrigger(e.target);
    if (!resolved || !resolved.handle) return;

    e.preventDefault();
    e.stopPropagation();

    // Debounce rapid clicks
    const now = Date.now();
    if (now - lastOpenTime < CONFIG.debounceDelay) return;
    lastOpenTime = now;

    const handle = resolved.handle.trim();
    if (!handle) return;
    if (dialog.open) swap(handle, resolved.title);
    else open(handle, resolved.title);
  }

  /**
   * Show an element from this page in the drawer (data-page-drawer-element="<id>").
   */
  function onElementTriggerClick(e) {
    const trigger = e.target.closest('[data-page-drawer-element]');
    if (!trigger || !dialog) return;
    const el = document.getElementById(trigger.dataset.pageDrawerElement);
    if (!el || el.closest(CONFIG.selectors.dialog)) return; // not on this page: the #anchor href applies
    e.preventDefault();
    openElement(el, trigger.dataset.pageDrawerTitle || trigger.textContent.trim());
  }

  /**
   * Put a borrowed element back where it came from. Runs before any drawer content is
   * replaced or cleared, which would otherwise destroy it.
   */
  function returnBorrowed() {
    if (!borrowed) return;
    borrowed.marker.replaceWith(borrowed.el);
    borrowed = null;
  }

  /**
   * Prefetch on intent: warm the cache for a trigger the visitor is hovering,
   * focusing, or touch-starting, so a subsequent click opens without a fetch.
   * Dedup lives in fetchPage() (cache + in-flight), so calling preload()
   * repeatedly (pointerover fires often) never double-fetches; the guards here
   * just skip the call when there's already a completed or pending result.
   * A failed prefetch leaves no sentinel, so a later hover can retry.
   */
  function onPrefetchIntent(e) {
    const resolved = resolveTrigger(e.target);
    if (!resolved || !resolved.handle) return;
    const handle = resolved.handle.trim();
    if (!handle || cache.has(handle) || inflight.has(handle)) return;
    preload(handle);
  }

  /**
   * Handle backdrop click
   */
  function onBackdropClick(e) {
    if (e.target === dialog && !isClosing) {
      close();
    }
  }

  /**
   * Handle cancel event (native Escape key).
   * Intercepted so we can play the slide-out animation instead of the
   * instant native close.
   */
  function onCancel(e) {
    e.preventDefault();
    if (!isClosing) close();
  }

  /**
   * Handle browser back button
   */
  function onPopState(e) {
    if (dialog.open && !isClosing) {
      close(true); // Skip history manipulation
    }
  }

  /**
   * Push the drawer's history entry. Back-to-close is a convenience, not a requirement:
   * WebKit throws SecurityError once a page exceeds its pushState budget (observed at ~49
   * calls / 10s, shared with every other script on the page), and on iOS the body lock is
   * position:fixed — so an unguarded throw here freezes the storefront with no drawer on
   * screen to close. Losing one Back press is the cheaper failure.
   * @param {object} state - History state to push.
   */
  function pushDrawerState(state) {
    try {
      history.pushState(state, '', window.location.href);
    } catch (err) {
      console.warn('PageDrawer: history entry unavailable, Back-to-close disabled:', err.message);
    }
  }

  /**
   * Open the modal, register the history entry, then lock the body — in that order, which
   * is the only one of the three that has no failure mode. Each step is placed by what the
   * step after it cannot survive:
   *
   *   1. showModal() FIRST. It is the only genuinely throwable step (a disconnected dialog
   *      raises InvalidStateError — WHATWG HTML, "dialog.showModal()"), and going first
   *      means a throw has nothing to unwind: no entry pushed, no body locked.
   *   2. pushDrawerState() SECOND, while the body is still scrollable. Horizon patches
   *      history.pushState (assets/scroll-container.js) to replaceState the LIVE
   *      getScrollContainer().scrollTop into the outgoing entry; under the iOS lock the
   *      body is position:fixed and that reads 0, so pushing after the lock would record
   *      0 as the reader's position and Back would restore them to the top of the page.
   *      Its own catch keeps a pushState quota failure non-fatal.
   *   3. The body lock LAST. classList.add + style.setProperty cannot throw, so the body
   *      is never left fixed across a step that can — the defect this whole change exists
   *      to close (page frozen, no dialog on screen, no way to recover on iOS).
   *
   * Reordering these is a correctness change, not a style one.
   */
  function showModalAndLock(state) {
    try {
      dialog.showModal();
    } catch (err) {
      // Nothing pushed and nothing locked yet — only the scrollRestoration takeover
      // (set by the caller before this point) needs handing back.
      if (prevScrollRestoration !== null) {
        history.scrollRestoration = prevScrollRestoration;
        prevScrollRestoration = null;
      }
      throw err;
    }

    pushDrawerState(state);

    document.body.style.setProperty('--page-drawer-scroll-position', `-${scrollY}px`);
    document.body.classList.add(CONFIG.classes.bodyLock);
  }

  /**
   * Clear a body lock left behind by a failed open. Belt-and-braces: the ordering above
   * closes the known window, but a lock held while no dialog is open is ALWAYS a bug, and
   * on iOS it is an unrecoverable one. Reading window.scrollY while the body is fixed
   * yields 0, so a stale lock also corrupts the scroll position the next open captures —
   * clear it before that read, not after.
   */
  function clearStaleBodyLock() {
    if (dialog?.open || !document.body.classList.contains(CONFIG.classes.bodyLock)) return;

    const stale = document.body.style.getPropertyValue('--page-drawer-scroll-position');
    document.body.classList.remove(CONFIG.classes.bodyLock);
    document.body.style.removeProperty('--page-drawer-scroll-position');
    // Unlocking a position:fixed body drops the page to offset 0; put the reader back.
    const restored = Math.abs(parseInt(stale, 10)) || 0;
    if (restored) window.scrollTo({ top: restored, left: 0, behavior: 'instant' });
  }

  /**
   * Open drawer
   */
  async function open(pageHandle, customTitle) {
    if (dialog.open || isClosing) return;

    clearStaleBodyLock();

    // Cancel any close timeout left pending (e.g. resumed after a bfcache restore) so it
    // can't fire dialog.close() on the drawer we're about to open.
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    // Sanitize handle - only allow safe characters
    const safeHandle = encodeURIComponent(pageHandle).replace(/%2F/g, '/');
    currentHandle = safeHandle;

    // Create new abort controller
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    const signal = abortController.signal;

    // Store scroll position and previous focus
    scrollY = window.scrollY;
    previousFocus = document.activeElement;

    // Set initial loading state
    dialog.setAttribute('aria-busy', 'true');
    titleEl.textContent = customTitle || 'Loading…';
    showLoading();

    // Take over scroll restoration so the browser's own history-based restore
    // (default 'auto') can't fight our manual restore on close. Handed back on
    // every close path. Same rationale as dialog.js / cart-drawer.js.
    prevScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';

    // Open the dialog (showModal makes the rest of the page inert + traps focus), register
    // the Back-to-close entry, then lock the body. See showModalAndLock() for why that
    // order is the only safe one.
    showModalAndLock({ pageDrawer: safeHandle });

    // Focus close button after animation frame
    requestAnimationFrame(() => {
      if (closeBtn && dialog.open) {
        closeBtn.focus();
      }
    });

    await load(safeHandle, customTitle, signal);
  }

  /**
   * Replace the open drawer's page with another one (a /pages/ link inside drawer content).
   * Keeps the one history entry open() pushed, so Back still closes the drawer.
   */
  async function swap(pageHandle, customTitle) {
    if (isClosing) return;
    const safeHandle = encodeURIComponent(pageHandle).replace(/%2F/g, '/');
    if (safeHandle === currentHandle) return;
    currentHandle = safeHandle;
    returnBorrowed();
    if (abortController) abortController.abort();
    abortController = new AbortController();

    dialog.setAttribute('aria-busy', 'true');
    titleEl.textContent = customTitle || 'Loading…';
    showLoading();
    // The clicked link is gone with the old content; keep focus inside the drawer.
    if (closeBtn) closeBtn.focus();
    await load(safeHandle, customTitle, abortController.signal);
  }

  /**
   * Fetch a page and render it into the open drawer (shared by open() and swap()).
   */
  async function load(safeHandle, customTitle, signal) {
    try {
      const content = await fetchPage(safeHandle, signal);

      // Check if still relevant (not closing, not closed, not a different page).
      // isClosing matters specifically for a JOINED prefetch: it was started with
      // a null signal, so close()'s abort can't cancel it, and during the 250ms
      // close animation dialog.open is still true and currentHandle still matches.
      // Without this guard a late-resolving prefetch would render into the drawer
      // as it slides out (the content is wiped on close anyway — a cosmetic flash).
      if (!dialog.open || isClosing || currentHandle !== safeHandle) return;

      // Empty / section-built page (no page.content): degrade gracefully
      // to a normal full-page navigation instead of showing a blank drawer.
      if (content.empty) {
        hardCloseAndNavigate(safeHandle);
        return;
      }

      titleEl.textContent = customTitle || content.title;
      renderContent(content.html);
      dialog.setAttribute('aria-busy', 'false');

      // Scroll body to top
      bodyEl.scrollTop = 0;
    } catch (err) {
      // A genuine user-close aborts with a plain AbortError — ignore it silently.
      // A fetch TIMEOUT aborts with a TimeoutError (distinct reason, set in
      // fetchPage) and falls through to showError() so the stuck "Loading…" state
      // is replaced with an error + "View page" fallback.
      if (err.name === 'AbortError') return;

      console.warn('PageDrawer error:', err.message);

      if (dialog.open && !isClosing && currentHandle === safeHandle) {
        showError(safeHandle);
        dialog.setAttribute('aria-busy', 'false');
      }
    }
  }

  /**
   * Fetch page content with timeout.
   * Uses the page.drawer alternate template (?view=drawer) which returns a
   * minimal, known wrapper — no full-page parsing / selector guessing.
   * Returns { empty: true } for pages with no drawer-able content.
   */
  async function fetchPage(handle, signal) {
    // Completed result → return it immediately.
    if (cache.has(handle)) {
      return cache.get(handle);
    }
    // In-flight request → JOIN the existing fetch instead of starting a second
    // one. This is what makes a hover/touchstart prefetch followed by a quick
    // click resolve from the SAME request (no duplicate fetch, no extra spinner)
    // instead of racing two requests. The joiner shares the first caller's fetch;
    // a close mid-flight can't abort a fetch it didn't start, but open()'s
    // post-await guard skips rendering and the result just warms the cache.
    if (inflight.has(handle)) {
      return inflight.get(handle);
    }
    const request = performFetch(handle, signal).finally(() => {
      // Identity-guarded cleanup: only remove our own entry. A stale settle must
      // not delete a NEWER request for the same handle (e.g. one started after
      // clearCache() emptied the map while this request was still in flight) —
      // otherwise dedupe breaks and a later open() spawns a duplicate fetch.
      if (inflight.get(handle) === request) inflight.delete(handle);
    });
    inflight.set(handle, request);
    return request;
  }

  /**
   * Perform the actual page fetch + parse. No dedup — callers go through
   * fetchPage(), which layers cache + in-flight dedup on top. Resolves to
   * { empty } or { empty:false, title, html }; throws on HTTP/timeout error.
   */
  async function performFetch(handle, signal) {
    // Snapshot the cache generation at fetch start. If clearCache() bumps it
    // while this request is in flight, the post-resolve cache.set calls below
    // are skipped — a prefetch can't silently repopulate a cache that was cleared.
    const generation = cacheGeneration;

    // Create timeout controller. Abort with a distinct TimeoutError reason so
    // open()'s catch can distinguish a fetch timeout (→ show the error fallback)
    // from a genuine user-close abort (→ silent).
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(
      () => timeoutController.abort(new DOMException('Page drawer fetch timed out', 'TimeoutError')),
      CONFIG.fetchTimeout
    );

    // Link external signal to timeout controller
    const onAbort = () => timeoutController.abort();
    if (signal) {
      signal.addEventListener('abort', onAbort);
    }

    try {
      const response = await fetch(`/pages/${handle}?view=drawer`, {
        signal: timeoutController.signal,
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      // Clear the timeout only AFTER the body is fully read: fetch() resolves on
      // headers, so clearing it earlier left a stalled body read uncovered (drawer
      // stuck on "Loading…"). Keeping the abort armed through response.text() routes a
      // body-read stall to the TimeoutError path → error fallback. A thrown
      // !response.ok is cleaned up by the catch below.
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onAbort);
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Deterministic targets from templates/page.drawer.liquid
      const wrapper = doc.querySelector('#drawer-page');
      const contentEl = doc.querySelector('#drawer-page-content');
      const innerHtml = contentEl ? contentEl.innerHTML : '';

      // Section-built / empty pages: tell the caller to navigate normally.
      if (isTrivialContent(contentEl)) {
        const empty = { empty: true };
        if (generation === cacheGeneration) cache.set(handle, empty);
        return empty;
      }

      // A drawer view brings its own <link rel="stylesheet"> tags. Rendered as-is, the content
      // paints unstyled (full-size images, bare lists) until they arrive, so load them into
      // <head> first. A hover/touch prefetch therefore warms the CSS as well as the HTML.
      await loadStyles(contentEl);

      const title =
        (wrapper && wrapper.dataset.drawerTitle && wrapper.dataset.drawerTitle.trim()) ||
        formatHandle(handle);

      const result = {
        empty: false,
        title,
        html: innerHtml,
      };

      // Cache the result (unless clearCache() ran while this fetch was in flight)
      if (generation === cacheGeneration) cache.set(handle, result);

      return result;
    } catch (err) {
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onAbort);
      throw err;
    }
  }

  /**
   * Resolve once every stylesheet linked inside the fetched content is applied to this page.
   * A sheet the page already has (outside the drawer, whose own copies go with each render)
   * counts; any other is appended to <head>, where it outlives the drawer content, and awaited.
   * Error and a 3s cap also resolve: a slow or failed sheet must not hold the drawer on
   * "Loading…", it only brings back the unstyled first paint.
   * @param {Element} contentEl - the #drawer-page-content element from the fetched doc
   */
  function loadStyles(contentEl) {
    const onPage = [...document.querySelectorAll('link[rel="stylesheet"]')].filter(
      (l) => !l.closest(CONFIG.selectors.dialog)
    );
    const hrefs = [...contentEl.querySelectorAll('link[rel="stylesheet"][href]')].map(
      (l) => new URL(l.getAttribute('href'), location.href).href
    );
    return Promise.all(
      hrefs.map((href) => {
        const existing = onPage.find((l) => l.href === href);
        if (existing && existing.sheet) return null;
        const link =
          existing || document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'stylesheet', href }));
        return new Promise((resolve) => {
          link.addEventListener('load', resolve, { once: true });
          link.addEventListener('error', resolve, { once: true });
          setTimeout(resolve, 3000);
        });
      })
    );
  }

  /**
   * Detect empty / section-built page content. DOM-based (not a regex) so it is
   * robust to tag case, all element types (media/table/audio/embed/…), and
   * comments: any element child OR any non-whitespace text means real content;
   * only a truly empty/whitespace body (a section-built page with no page.content)
   * is trivial → the caller navigates instead of opening a blank drawer.
   * @param {Element|null} el - the #drawer-page-content element from the fetched doc
   */
  function isTrivialContent(el) {
    if (!el) return true;
    if (el.children.length > 0) return false;
    return el.textContent.replace(/ /g, ' ').trim().length === 0;
  }

  /**
   * Close the half-open drawer and navigate to the canonical page URL.
   * Used when the fetched page has no drawer-able content. open() already pushed a
   * same-URL history entry; we replace() over it (not assign()) so no duplicate is
   * left behind and Back returns the user to the true originating page.
   */
  function hardCloseAndNavigate(handle) {
    // We're leaving the page; make onDialogClose a no-op so closing the modal below
    // doesn't fire a spurious history.back() into the navigation we're about to do.
    suppressDialogClose = true;
    document.body.classList.remove(CONFIG.classes.bodyLock);
    document.body.style.removeProperty('--page-drawer-scroll-position');
    if (prevScrollRestoration !== null) {
      history.scrollRestoration = prevScrollRestoration;
      prevScrollRestoration = null;
    }
    // Close the still-open modal so a bfcache restore of this page can't return with
    // a stuck-open drawer.
    if (dialog.open) dialog.close();
    // replace() (not assign()) collapses the same-URL entry open() pushed, so Back
    // from the destination returns to the true origin instead of a stale duplicate.
    window.location.replace(`/pages/${handle}`);
  }

  /**
   * Format handle to readable title
   */
  function formatHandle(handle) {
    return handle.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /**
   * Render content
   */
  function renderContent(html) {
    bodyEl.innerHTML = `<div class="page-drawer__content">${html}</div>`;
    // Make all links inside drawer open in a new tab
    bodyEl.querySelectorAll('a[href]').forEach((a) => {
      if (!a.getAttribute('href').startsWith('#')) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener');
      }
    });
  }

  /**
   * Show loading state
   */
  function showLoading() {
    bodyEl.innerHTML = `
    <div class="page-drawer__loading" role="status" aria-label="Loading content">
      <div class="page-drawer__spinner" aria-hidden="true"></div>
      <p class="page-drawer__loading-text">Loading…</p>
    </div>`;
  }

  /**
   * Show error state
   */
  function showError(handle) {
    titleEl.textContent = 'Error';
    const safeHandle = handle.replace(/[<>"'&]/g, '');
    bodyEl.innerHTML = `
    <div class="page-drawer__error" role="alert">
      <div class="page-drawer__error-icon" aria-hidden="true">⚠️</div>
      <p class="page-drawer__error-message">Unable to load content.</p>
      <a href="/pages/${safeHandle}" class="page-drawer__error-link">View page →</a>
    </div>`;
  }

  /**
   * Close drawer
   * @param {boolean} skipHistory - Skip history manipulation (for popstate)
   */
  function close(skipHistory = false) {
    if (!dialog.open || isClosing) return;

    isClosing = true;
    // Remember whether onDialogClose should pop history. When close() is driven by
    // popstate (Back button) the entry is already gone, so we must not pop again.
    skipHistoryOnClose = skipHistory;

    // Abort any pending fetch
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    // History pop + scroll restore happen AFTER the body unlock in onDialogClose(),
    // mirroring cart-drawer/dialog.js so the browser can't re-zero scroll on the
    // back() navigation.

    // Animate close
    dialog.classList.add(CONFIG.classes.closing);

    closeTimer = setTimeout(() => {
      closeTimer = null;
      dialog.classList.remove(CONFIG.classes.closing);
      dialog.close();
      isClosing = false;
    }, CONFIG.animationDuration);
  }

  /**
   * Handle native dialog close event
   */
  function onDialogClose() {
    returnBorrowed();

    // During the empty-page hard navigation we've already cleaned up and are leaving;
    // skip the scroll/history restore work.
    if (suppressDialogClose) {
      suppressDialogClose = false;
      return;
    }

    // Clean up state
    isClosing = false;
    currentHandle = null;

    // Unlock body scroll
    document.body.classList.remove(CONFIG.classes.bodyLock);
    document.body.style.removeProperty('--page-drawer-scroll-position');

    // Restore scroll position BEFORE any history navigation, while scrollRestoration
    // is still 'manual' so nothing competes with this call. behavior:'instant' is
    // required: the theme sets html { scroll-behavior: smooth } (base.css), so the
    // default 2-arg scrollTo would animate the viewport from top back to scrollY.
    window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });

    const handBackRestoration = () => {
      if (prevScrollRestoration !== null) {
        history.scrollRestoration = prevScrollRestoration;
        prevScrollRestoration = null;
      }
    };
    if (!skipHistoryOnClose && history.state && history.state.pageDrawer) {
      // Pop the entry open() pushed. Keep scrollRestoration 'manual' until the
      // resulting popstate fires, then hand it back — otherwise the browser can
      // auto-restore scroll to the locked top-of-page position on that navigation.
      const afterPop = () => {
        window.removeEventListener('popstate', afterPop);
        handBackRestoration();
      };
      window.addEventListener('popstate', afterPop);
      history.back();
    } else {
      handBackRestoration();
    }
    skipHistoryOnClose = false;

    // Restore focus to trigger element
    if (previousFocus && typeof previousFocus.focus === 'function') {
      try {
        previousFocus.focus({ preventScroll: true });
      } catch (e) {
        // Focus failed, ignore
      }
    }
    previousFocus = null;

    // Clear content after animation completes
    requestAnimationFrame(() => {
      if (!dialog.open) {
        bodyEl.innerHTML = '';
        titleEl.textContent = '';
      }
    });
  }

  /**
   * Preload a page for instant opens
   */
  function preload(handle) {
    if (!handle || cache.has(handle)) {
      return Promise.resolve();
    }

    return fetchPage(handle, null).catch(() => {
      // Silently fail preload
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /**
   * Open drawer with inline HTML content (no fetch)
   * @param {string} html - HTML content to display
   * @param {string} title - Title for the drawer header
   * @param {Element} [titleElement] - Trusted server-rendered title to clone with its typography
   */
  function openWithContent(html, title, titleElement) {
    openFilled(() => renderContent(html || '<p>No content available.</p>'), title, titleElement, 'inline-content');
  }

  /**
   * Open drawer showing an element from this page, moved in (see returnBorrowed()).
   * @param {Element} el
   * @param {string} title
   */
  function openElement(el, title) {
    openFilled(
      () => {
        borrowed = { el, marker: document.createComment('page-drawer: borrowed element') };
        el.replaceWith(borrowed.marker);
        // Not wrapped in .page-drawer__content: its article typography (p, li, img and link
        // margins and colours) would restyle the borrowed widget, which brings its own.
        bodyEl.replaceChildren(el);
      },
      title,
      null,
      'element'
    );
  }

  /**
   * Shared by openWithContent() and openElement(): fill() puts the content in the body.
   */
  function openFilled(fill, title, titleElement, stateName) {
    if (dialog.open || isClosing) return;

    clearStaleBodyLock();

    // Cancel any close timeout left pending (see open()) so a stale slide-out timer can't
    // close the drawer we're about to open.
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    // Store scroll position and previous focus
    scrollY = window.scrollY;
    previousFocus = document.activeElement;

    // Set content immediately (no loading state needed)
    titleEl.textContent = title || '';
    if (titleElement) titleEl.replaceChildren(titleElement.cloneNode(true));
    fill();
    dialog.setAttribute('aria-busy', 'false');

    // Take over scroll restoration (see open()); handed back in onDialogClose().
    prevScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';

    // Open the dialog, register the Back-to-close entry, then lock the body. Locking last
    // also covers renderContent() above, which parses caller-supplied markup: a throw
    // there while the body was fixed would freeze the page with nothing on screen.
    // See showModalAndLock().
    try {
      showModalAndLock({ pageDrawer: stateName });
    } catch (err) {
      returnBorrowed(); // never opened, so onDialogClose won't run to return it
      throw err;
    }

    // Focus close button and scroll to top after animation frame
    requestAnimationFrame(() => {
      if (closeBtn && dialog.open) {
        closeBtn.focus();
      }
      bodyEl.scrollTop = 0;
    });
  }

  // Public API
  window.PageDrawer = {
    open,
    openWithContent,
    openElement,
    close: () => close(false),
    preload,
    isOpen: () => dialog?.open ?? false,
    clearCache: () => {
      cache.clear();
      inflight.clear();
      cacheGeneration += 1;
    },
  };
})();
